import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { HiSearch, HiPlus } from 'react-icons/hi';
import { Modal, Button, Table, TableColumn } from '@/components/common';
import { formatQuantity } from '@/utils/number-format';
import { useUIStore } from '@/store/uiStore';
import { cuttingApi } from '@/api/endpoints/cutting';
import { 
  CutConfig, 
  CutConfigLine, 
  CutType, 
  CreateCuttingOrderLineRequest 
} from '@/types/cutting';

interface CuttingFormLineModalProps {
  cutType: CutType;
  onAddLines?: (lines: CreateCuttingOrderLineRequest[]) => void;
}

const CuttingFormLineModal: React.FC<CuttingFormLineModalProps> = ({
  cutType,
  onAddLines,
}) => {
  const { t } = useTranslation();

  const { modals, closeModal, openModal } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.cuttingFormLine;

  const [searchQuery, setSearchQuery] = useState('');
  const [availableConfigs, setAvailableConfigs] = useState<CutConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [selectedConfig, setSelectedConfig] = useState<CutConfig | null>(null);

  // Cut type labels
  const cutTypeLabels: Record<CutType, string> = {
    cut_wood: 'Cắt gỗ',
    cut_mica: 'Cắt mica',
    cut_metal: 'Cắt kim loại',
    cut_plastic: 'Cắt nhựa',
    cut_glass: 'Cắt kính',
  };

  // Fetch available configs when modal opens or search changes
  useEffect(() => {
    if (isOpen) {
      fetchConfigs();
    }
  }, [isOpen, cutType, searchQuery]);

  // Update selected config when selection changes
  useEffect(() => {
    if (selectedConfigId) {
      const config = availableConfigs.find(c => c.id === selectedConfigId);
      setSelectedConfig(config || null);
    } else {
      setSelectedConfig(null);
    }
  }, [selectedConfigId, availableConfigs]);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const configs = await cuttingApi.searchCutConfigsBySize(cutType, searchQuery, 20);
      setAvailableConfigs(configs);
    } catch (error) {
      console.error('Error fetching cut configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('cuttingFormLine');
    setSearchQuery('');
    setSelectedConfigId('');
    setSelectedConfig(null);
    setAvailableConfigs([]);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setSelectedConfigId(''); // Reset selection when searching
  };

  const handleConfigSelect = (configId: string) => {
    setSelectedConfigId(configId);
  };

  const handleCreateNewConfig = () => {
    handleClose();
    openModal('cuttingConfig');
  };

  const handleSave = () => {
    if (!selectedConfig || !onAddLines) {
      return;
    }

    // Convert cut config lines to cutting order lines
    const orderLines: CreateCuttingOrderLineRequest[] = selectedConfig.cutConfigLines.map(line => ({
      materialName: `${selectedConfig.materialType} ${line.finalSize}`,
      cutType: selectedConfig.cutType,
      quantity: line.quantity,
      unit: line.unit,
      text: line.notes || `${selectedConfig.name} - ${line.finalSize}`,
      cutConfigId: selectedConfig.id,
    }));

    onAddLines(orderLines);
    handleClose();
  };

  const handleSaveAndClose = () => {
    handleSave();
  };

  // Config list table columns
  const configColumns: TableColumn<CutConfig>[] = [
    {
      key: 'name',
      title: 'Tên cấu hình',
      render: (value, record) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{record.materialType}</div>
        </div>
      ),
    },
    {
      key: 'usageCount',
      title: 'Đã dùng',
      width: 80,
      align: 'center',
      render: (value) => (
        <span className="text-sm text-gray-600">{value} lần</span>
      ),
    },
    {
      key: 'lines',
      title: 'Số dòng',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <span className="text-sm text-gray-600">{record.cutConfigLines.length}</span>
      ),
    },
  ];

  // Cut lines table columns
  const cutLineColumns: TableColumn<CutConfigLine>[] = [
    {
      key: 'finalSize',
      title: 'Kích thước',
      dataIndex: 'finalSize',
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: 'quantity',
      title: 'Số lượng',
      dataIndex: 'quantity',
      width: 100,
      align: 'center',
      render: (value, record) => (
        <span className="text-sm text-gray-900">{formatQuantity(value)} {record.unit}</span>
      ),
    },
    {
      key: 'notes',
      title: 'Ghi chú',
      dataIndex: 'notes',
      render: (value) => (
        <span className="text-sm text-gray-600">{value || '-'}</span>
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('tooltips.cutting.selectCutConfig')}
      size="xl"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <div className="space-y-6">
        {/* Cut Config Search Field - positioned top, next to cut type selector as specified */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm font-medium text-gray-700">
              Loại cắt: <span className="text-blue-600">{cutTypeLabels[cutType]}</span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="input-field w-64 pl-10"
                placeholder="Tìm kiếm theo kích thước..."
                disabled={loading}
              />
              <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleCreateNewConfig}
              icon={<HiPlus className="h-4 w-4" />}
            >
              Tạo mới
            </Button>
          </div>
        </div>

        {/* Dropdown Config List - shows available configs as specified */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Cấu hình có sẵn</h3>
          
          <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
            <Table
              columns={configColumns}
              data={availableConfigs}
              loading={loading}
              rowKey="id"
              size="sm"
              
              onRow={(record) => ({
                className: `cursor-pointer ${selectedConfigId === record.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`,
                onClick: () => handleConfigSelect(record.id),
              })}
              emptyText="Không tìm thấy cấu hình nào. Thử tìm kiếm khác hoặc tạo cấu hình mới."
            />
          </div>
        </div>

        {/* Selected Config Info - displays selected config details as specified */}
        {selectedConfig && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Cấu hình đã chọn</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-700 font-medium">Tên:</span> {selectedConfig.name}
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Vật liệu:</span> {selectedConfig.materialType}
                </div>
                <div className="col-span-2">
                  <span className="text-blue-700 font-medium">Mô tả:</span> {selectedConfig.description || '-'}
                </div>
              </div>
            </div>

            {/* Cut Lines Table - shows actual cut lines pulled from config as specified */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Chi tiết cắt</h4>
              <div className="border border-gray-200 rounded-lg">
                <Table
                  columns={cutLineColumns}
                  data={selectedConfig.cutConfigLines}
                  rowKey="id"
                  size="sm"
  
                  emptyText="Cấu hình này chưa có dòng cắt nào."
                />
              </div>
            </div>
          </div>
        )}

        {/* Buttons - Save, Save and Close positioned bottom-right as specified */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Hủy
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={handleSave}
              disabled={!selectedConfig || loading}
            >
              Thêm vào lệnh cắt
            </Button>
            
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveAndClose}
              disabled={!selectedConfig || loading}
            >
              Thêm và đóng
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CuttingFormLineModal;
